import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { getLegalPages, updateLegalPage } from '@/lib/api';
import { Save, FileText, Globe } from 'lucide-react';

const PAGE_LABELS = {
  support: { icon: '?', label: 'Support' },
  agb: { icon: '§', label: 'AGB' },
  impressum: { icon: 'i', label: 'Impressum' },
  datenschutz: { icon: 'D', label: 'Datenschutz' },
  datenschutzeinstellungen: { icon: 'E', label: 'Datenschutzeinstellungen' },
};

const PAGE_ORDER = ['support', 'agb', 'impressum', 'datenschutz', 'datenschutzeinstellungen'];

export const LegalPagesTab = ({ t }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editData, setEditData] = useState({});
  const [activePage, setActivePage] = useState('support');
  const [lang, setLang] = useState('de');

  const loadPages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getLegalPages();
      setPages(res.data);
      const initial = {};
      res.data.forEach(p => {
        initial[p.slug] = { ...p };
      });
      setEditData(initial);
    } catch {
      toast.error(t('admin.legal.loadError'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPages(); }, [loadPages]);

  const handleSave = async (slug) => {
    setSaving(slug);
    try {
      const data = editData[slug];
      await updateLegalPage(slug, {
        title_de: data.title_de,
        title_en: data.title_en,
        content_de: data.content_de,
        content_en: data.content_en,
      });
      toast.success(`${PAGE_LABELS[slug]?.label || slug} ${t('admin.legal.pageSavedSuffix')}`);
      loadPages();
    } catch {
      toast.error(t('admin.legal.saveError'));
    } finally {
      setSaving(null);
    }
  };

  const updateField = (slug, field, value) => {
    setEditData(prev => ({
      ...prev,
      [slug]: { ...prev[slug], [field]: value },
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardHeader><Skeleton className="h-6 w-48" /></CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    );
  }

  const orderedPages = PAGE_ORDER.filter(slug => editData[slug]);

  return (
    <Card data-testid="legal-pages-admin">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          {t('admin.legalPages') || 'Rechtliche Seiten'}
        </CardTitle>
        <CardDescription>
          {t('admin.legalPagesDesc') || 'Support, AGB, Impressum, Datenschutz und Datenschutzeinstellungen bearbeiten'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Language toggle */}
        <div className="flex items-center gap-2 mb-4">
          <Globe className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm text-muted-foreground">{t('admin.legal.language')}</span>
          <div className="flex border rounded-md">
            <Button
              variant={lang === 'de' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setLang('de')}
              className="rounded-r-none text-xs px-3"
              data-testid="legal-lang-de"
            >
              DE
            </Button>
            <Button
              variant={lang === 'en' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setLang('en')}
              className="rounded-l-none text-xs px-3"
              data-testid="legal-lang-en"
            >
              EN
            </Button>
          </div>
        </div>

        <Tabs value={activePage} onValueChange={setActivePage}>
          <TabsList className="flex flex-wrap h-auto gap-1 bg-muted/50 p-1 rounded-lg mb-4" data-testid="legal-pages-tabs">
            {orderedPages.map(slug => (
              <TabsTrigger key={slug} value={slug} className="text-xs sm:text-sm gap-1" data-testid={`legal-tab-${slug}`}>
                {PAGE_LABELS[slug]?.label || slug}
              </TabsTrigger>
            ))}
          </TabsList>

          {orderedPages.map(slug => {
            const pageData = editData[slug];
            if (!pageData) return null;
            const titleField = `title_${lang}`;
            const contentField = `content_${lang}`;

            return (
              <TabsContent key={slug} value={slug}>
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm font-medium">
                      {t('admin.legalPageTitle') || 'Seitentitel'} ({lang.toUpperCase()})
                    </Label>
                    <Input
                      value={pageData[titleField] || ''}
                      onChange={(e) => updateField(slug, titleField, e.target.value)}
                      className="mt-1"
                      data-testid={`legal-title-input-${slug}`}
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">
                      {t('admin.legalPageContent') || 'Seiteninhalt (HTML)'} ({lang.toUpperCase()})
                    </Label>
                    <textarea
                      value={pageData[contentField] || ''}
                      onChange={(e) => updateField(slug, contentField, e.target.value)}
                      rows={14}
                      className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      data-testid={`legal-content-input-${slug}`}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('admin.legal.htmlTagsAllowed')}
                    </p>
                  </div>

                  {/* Preview */}
                  <div>
                    <Label className="text-sm font-medium mb-2 block">{t('admin.legalPreview') || 'Vorschau'}</Label>
                    <div className="border rounded-lg p-4 bg-white dark:bg-slate-900">
                      <h3 className="font-bold text-lg mb-3">{pageData[titleField]}</h3>
                      <div
                        className="prose prose-sm prose-slate dark:prose-invert max-w-none"
                        dangerouslySetInnerHTML={{ __html: pageData[contentField] || '' }}
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      onClick={() => handleSave(slug)}
                      disabled={saving === slug}
                      className="bg-orange-500 hover:bg-orange-600 gap-2"
                      data-testid={`legal-save-${slug}`}
                    >
                      <Save className="h-4 w-4" />
                      {saving === slug ? (t('common.saving') || 'Speichern...') : (t('common.save') || 'Speichern')}
                    </Button>
                  </div>
                </div>
              </TabsContent>
            );
          })}
        </Tabs>
      </CardContent>
    </Card>
  );
};
